import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Clock, Check, ArrowRight, Phone } from "lucide-react";
import PageShell from "./PageShell";
import { Reveal, EASE } from "../components/landing/Reveal";

const PLANS = [
  {
    type: "Individual Coaching",
    minutes: 45,
    price: 60,
    features: [
      "A session built around your goals and fitness level",
      "45 focused minutes — every one of them counts",
      "Strength, movement and mobility that fit real life",
    ],
  },
  {
    type: "Individual Coaching",
    minutes: 30,
    price: 40,
    features: [
      "A session built around your goals and fitness level",
      "30 focused minutes — every one of them counts",
      "Strength, movement and mobility that fit real life",
    ],
  },
  {
    type: "Group Fitness",
    minutes: 45,
    price: 25,
    features: [
      "Fun, challenging workouts that keep you motivated",
      "45 minutes of coached group training",
      "Community energy and built-in accountability",
    ],
    deal: "Special deals when you bring your own group!",
  },
];

const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <PageShell title="Pricing">
      <section className="relative py-20 sm:py-28 bg-mint overflow-hidden" data-testid="pricing-section">
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-[26rem] h-[26rem] rounded-full bg-white/30 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -left-24 w-[26rem] h-[26rem] rounded-full bg-brand/10 blur-3xl"
        />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-brand-dark mb-4 flex items-center justify-center gap-3">
              <span className="text-charcoal/40">06</span>
              <span className="h-px w-10 bg-charcoal/30" aria-hidden="true" />
              Pricing
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-charcoal">
              Simple, Honest Pricing
            </h1>
            <p className="mt-5 max-w-xl mx-auto text-base sm:text-lg text-charcoal/70 leading-relaxed">
              No contracts, no confusion — just great coaching at a fair price.
            </p>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {PLANS.map((p, i) => (
              <Reveal key={p.minutes} delay={0.12 * i}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.35, ease: EASE }}
                  className="relative overflow-hidden rounded-[2.5rem] bg-charcoal text-sand p-8 sm:p-10 grain shadow-[0_30px_80px_rgb(0,0,0,0.25)] h-full"
                  data-testid={`pricing-card-${p.price}`}
                >
                  <div
                    aria-hidden="true"
                    className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand/20 blur-3xl"
                  />
                  <div className="relative">
                    <span className="inline-flex items-center gap-2 rounded-full bg-brand/15 text-brand text-xs font-bold uppercase tracking-[0.2em] px-4 py-2">
                      <Clock size={14} /> {p.type} · {p.minutes} minutes
                    </span>

                    <div className="mt-8 flex items-end gap-3">
                      <span
                        className="font-display font-black leading-none text-sand text-[5.5rem] sm:text-[6.5rem] tracking-tighter"
                        data-testid={`pricing-amount-${p.price}`}
                      >
                        <span className="text-brand align-top text-[3rem] sm:text-[3.5rem]">$</span>
                        {p.price}
                      </span>
                      <span className="pb-3 sm:pb-4 text-sand/50 font-medium text-sm">
                        per {p.minutes}-min session
                      </span>
                    </div>

                    <div className="mt-7 h-px bg-sand/10" aria-hidden="true" />

                    <ul className="mt-7 space-y-4">
                      {p.features.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-sand/80 text-sm">
                          <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0 mt-0.5">
                            <Check size={13} className="text-brand" />
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>

                    {p.deal && (
                      <p
                        className="mt-5 rounded-2xl bg-brand/15 text-brand text-sm font-bold px-4 py-3 text-center"
                        data-testid={`pricing-deal-${p.price}`}
                      >
                        {p.deal}
                      </p>
                    )}

                    <button
                      onClick={() => navigate("/contact")}
                      data-testid={`pricing-book-btn-${p.price}`}
                      className="group mt-9 w-full rounded-full bg-brand text-charcoal font-bold px-8 py-4 text-sm tracking-wide flex items-center justify-center gap-2 hover:bg-sand transition-colors duration-300"
                    >
                      BOOK YOUR SESSION
                      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.25}>
            <p className="mt-8 text-center text-sm text-charcoal/60" data-testid="pricing-note">
              Package rates available on request —{" "}
              <a
                href="tel:+14035055078"
                className="font-bold text-charcoal underline underline-offset-4 hover:text-brand transition-colors duration-200"
                data-testid="pricing-call-link"
              >
                <Phone size={12} className="inline -mt-0.5" /> (403) 505-5078
              </a>
            </p>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
};

export default PricingPage;
